import { motion, useReducedMotion } from "framer-motion";
import { ProposalRigFigure } from "./ProposalRigFigure";
import { fmtDate, relDays } from "@/lib/format";
import { cn } from "@/lib/utils";
import { stateMeta, TONE_DOT } from "./proposalUtils";

const ease = [0.22, 1, 0.36, 1];

/** Cover: eyebrow, title, personal message, to/from/validity, and the rig on the right. Dark navy – the only dark surface besides the closing panel. */
export const ProposalHero = ({ data }) => {
  const reduce = useReducedMotion();
  const q = data.quote;
  const meta = stateMeta(data);
  const sol = data.solution;
  const fade = (i) => (reduce ? {} : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease, delay: 0.05 + i * 0.07 } });
  const message = data.share?.message;

  return (
    <section className="proposal-cover proposal-grain -mt-16 text-white" data-testid="proposal-hero">
      <div className="mx-auto grid max-w-[1180px] gap-12 px-5 pb-24 pt-28 sm:px-8 lg:grid-cols-12 lg:items-end lg:pb-28 lg:pt-36">
        <div className="lg:col-span-7">
          <motion.div {...fade(0)} className="flex flex-wrap items-center gap-3 text-[12.5px]">
            <span className="font-mono tabular tracking-wide text-white/60" data-testid="proposal-quote-number">{q.quote_number} · Versjon {q.version}</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 font-medium text-white/90 ring-1 ring-white/10" data-testid="proposal-state-chip">
              <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT[meta.tone])} />
              {meta.label}
            </span>
          </motion.div>
          <motion.h1 {...fade(1)} className="proposal-display mt-6 text-[40px] font-semibold leading-[1.02] sm:text-[52px] lg:text-[64px]" data-testid="proposal-title">
            {q.title}
          </motion.h1>
          <motion.p {...fade(2)} className="mt-6 max-w-[600px] text-[16.5px] leading-7 text-white/75 sm:text-[17.5px]" data-testid="proposal-lede">
            {message || `Et komplett tilbud på midlertidig ${data.project?.capacity_persons ? `innkvartering for ${data.project.capacity_persons} personer` : "modulløsning"} – levert, montert og driftsklar${data.project?.address ? ` på ${data.project.address}` : ""}.`}
          </motion.p>
          <motion.dl {...fade(3)} className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6 text-[14px] sm:grid-cols-3" data-testid="proposal-meta">
            <div>
              <dt className="text-[12px] text-white/50">Til</dt>
              <dd className="mt-1 font-medium">{data.customer?.name}</dd>
              {data.customer?.contact?.name && <dd className="text-white/65">Att. {data.customer.contact.name}</dd>}
            </div>
            <div>
              <dt className="text-[12px] text-white/50">Fra</dt>
              <dd className="mt-1 font-medium">{data.seller?.name || data.company?.name}</dd>
              <dd className="text-white/65">{data.seller?.title || data.company?.name}</dd>
            </div>
            <div>
              <dt className="text-[12px] text-white/50">{data.state === "accepted" ? "Akseptert" : "Gyldig til"}</dt>
              <dd className="mt-1 font-mono tabular font-medium" data-testid="proposal-valid-until">{fmtDate(data.state === "accepted" ? data.response?.accepted?.created_at || q.accepted_at : q.valid_until)}</dd>
              {data.state === "open" && q.valid_until && <dd className="text-white/65">{relDays(q.valid_until)}</dd>}
              {q.sent_at && data.state !== "open" && <dd className="text-white/65">Sendt {fmtDate(q.sent_at)}</dd>}
            </div>
          </motion.dl>
        </div>

        <motion.div {...fade(2)} className="lg:col-span-5">
          {sol?.preview?.boxes?.length ? (
            <figure className="relative overflow-hidden rounded-[28px] bg-white/[0.06] p-6 ring-1 ring-white/10 sm:p-8" data-testid="proposal-rig-figure">
              <div className="absolute inset-0 module-grid-bg opacity-60" aria-hidden="true" />
              <ProposalRigFigure preview={sol.preview} height={300} className="relative" testId="proposal-rig-preview" />
              <figcaption className="relative mt-5 flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-white/60">
                <span>{sol.source === "rig_design" ? `Riggdesign${sol.design_version ? ` v${sol.design_version}` : ""} – slik riggen er tegnet` : "Modulsammensetning – illustrasjon basert på prislinjene"}</span>
                <span className="font-mono tabular">{sol.kpi.modules} moduler{sol.kpi.beds ? ` · ${sol.kpi.beds} senger` : ""}</span>
              </figcaption>
            </figure>
          ) : (
            <div className="rounded-[28px] bg-white/[0.06] p-8 ring-1 ring-white/10" data-testid="proposal-rig-placeholder">
              <div className="text-[12.5px] text-white/50">Leveransen</div>
              <div className="mt-2 text-[22px] font-semibold leading-snug">{data.project?.name || q.title}</div>
              {data.project?.address && <div className="mt-2 text-[14px] text-white/65">{data.project.address}</div>}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
